import React from "react";
import { Card, CircularProgress, Tooltip } from "@material-ui/core";
import { PrimaryText } from "components/Common/PrimaryText";
import InfoTooltip from "./InfoTooltip";
import moment from "moment";
import { useDisplay } from "context/DisplayContext";

const RetentionTrends = ({ filter, dailySnapshots, loading, error }) => {
  const { isMobile } = useDisplay();

  // Remove local filter state since it's now controlled by parent

  if (loading) {
    return (
      <Card style={{ padding: 20, textAlign: "center" }}>
        <CircularProgress />
        <PrimaryText>Loading retention trends...</PrimaryText>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 20, textAlign: "center" }}>
        <PrimaryText color="error">
          Error loading retention trends: {error}
        </PrimaryText>
      </Card>
    );
  }

  // Show message if no snapshots data
  if (!dailySnapshots || dailySnapshots.length === 0) {
    return (
      <Card style={{ padding: 20, textAlign: "center" }}>
        <PrimaryText style={{ color: "#666" }}>
          Retention trends unavailable
        </PrimaryText>
        <PrimaryText fontSize={12} style={{ color: "#999", marginTop: 8 }}>
          No data available for the selected time period
        </PrimaryText>
      </Card>
    );
  }

  // Filter data based on selected option
  const getFilteredData = () => {
    if (!dailySnapshots || dailySnapshots.length === 0) return [];

    let startDate;

    if (filter === "6months") {
      // Last 6 months
      startDate = moment().subtract(6, "months");
    } else if (filter === "3months") {
      // Last 3 months
      startDate = moment().subtract(3, "months");
    } else if (filter === "1month") {
      // Last month
      startDate = moment().subtract(1, "month");
    } else if (filter === "2weeks") {
      // Last 2 weeks
      startDate = moment().subtract(2, "weeks");
    } else if (filter === "1week") {
      // Last week
      startDate = moment().subtract(1, "week");
    } else if (filter === "1year") {
      // Last year
      startDate = moment().subtract(1, "year");
    } else {
      // All time - no filtering
      return dailySnapshots.sort((a, b) => moment(a.date).diff(moment(b.date)));
    }

    return dailySnapshots
      .filter((snapshot) => moment(snapshot.date).isAfter(startDate))
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
  };

  // Format data for line chart
  const formatTrendData = () => {
    const filteredData = getFilteredData();
    if (filteredData.length === 0) return [];

    // For monthly view, aggregate by month
    if (
      filter === "6months" ||
      filter === "3months" ||
      filter === "1year" ||
      (filter === "alltime" && filteredData.length > 100)
    ) {
      const monthlyData = {};

      filteredData.forEach((snapshot) => {
        const month = moment(snapshot.date).format("YYYY-MM");
        if (!monthlyData[month]) {
          monthlyData[month] = {
            date: month,
            weeklyActiveUsers: 0,
            newUsers: 0,
            activeUsers: 0,
            count: 0,
          };
        }

        monthlyData[month].weeklyActiveUsers += snapshot.weeklyActiveUsers || 0;
        monthlyData[month].newUsers += snapshot.newUsers || 0;
        monthlyData[month].activeUsers += snapshot.activeUsers || 0;
        monthlyData[month].count++;
      });

      // Calculate averages
      return Object.values(monthlyData).map((month) => ({
        date: moment(month.date).format("MMM YYYY"),
        weeklyActiveUsers:
          month.count > 0
            ? Math.round(month.weeklyActiveUsers / month.count)
            : 0,
        newUsers: month.newUsers,
        activeUsers: Math.round(month.activeUsers / month.count),
      }));
    } else {
      // Weekly view or all time with limited data - show daily data (sample every 7th day for all time)
      const dataToShow =
        filter === "alltime" && filteredData.length > 50
          ? filteredData.filter((_, index) => index % 7 === 0)
          : filteredData;

      return dataToShow.map((snapshot) => ({
        date: moment(snapshot.date).format("MMM DD"),
        weeklyActiveUsers: snapshot.weeklyActiveUsers || 0,
        newUsers: snapshot.newUsers || 0,
        activeUsers: snapshot.activeUsers || 0,
      }));
    }
  };

  const trendData = formatTrendData();

  // Simple line chart implementation
  const LineChart = ({ data, dataKey, color, label }) => {
    if (data.length === 0) return null;

    const maxValue = Math.max(...data.map((item) => item[dataKey]));
    const minValue = Math.min(...data.map((item) => item[dataKey]));
    const range = maxValue - minValue || 1;

    return (
      <div style={{ marginBottom: 20 }}>
        <PrimaryText fontSize={14} fontWeight={600} style={{ marginBottom: 8 }}>
          {label}
        </PrimaryText>
        <div
          style={{
            height: isMobile ? 80 : 110,
            position: "relative",
            borderLeft: "1px solid #ddd",
            borderBottom: "1px solid #ddd",
            overflow: "hidden",
            margin: "0 10px",
            paddingTop: 10,
            width: "100%",
          }}
        >
          <svg
            style={{
              position: "absolute",
              top: 10,
              left: 0,
              width: "100%",
              height: "calc(100% - 10px)",
            }}
          >
            {/* Draw lines */}
            {data.slice(0, -1).map((item, index) => {
              const x1 = (index / (data.length - 1)) * 100;
              const y1 = 100 - ((item[dataKey] - minValue) / range) * 100;
              const x2 = ((index + 1) / (data.length - 1)) * 100;
              const y2 =
                100 - ((data[index + 1][dataKey] - minValue) / range) * 100;

              return (
                <line
                  key={index}
                  x1={`${x1}%`}
                  y1={`${y1}%`}
                  x2={`${x2}%`}
                  y2={`${y2}%`}
                  stroke={color}
                  strokeWidth="2"
                />
              );
            })}
          </svg>

          {/* Draw dots */}
          {data.map((item, index) => {
            const x = (index / (data.length - 1)) * 100;
            const y = 100 - ((item[dataKey] - minValue) / range) * 100;

            return (
              <Tooltip
                key={index}
                title={
                  <div style={{ padding: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {moment(item.date).format("MMM DD, YYYY")}
                    </div>
                    <div style={{ fontSize: 14, marginTop: 2 }}>
                      {label}: {item[dataKey]?.toLocaleString()}
                    </div>
                  </div>
                }
                arrow
                placement="top"
              >
                <div
                  style={{
                    position: "absolute",
                    left: `${x}%`,
                    top: `${y}%`,
                    width: isMobile ? 8 : 10,
                    height: isMobile ? 8 : 10,
                    backgroundColor: color,
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%)",
                    cursor: "pointer",
                    zIndex: 1,
                    border: "2px solid white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform =
                      "translate(-50%, -50%) scale(1.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translate(-50%, -50%) scale(1)";
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 8,
            margin: "8px 10px 0 10px",
          }}
        >
          <PrimaryText fontSize={12} style={{ color: "#666" }}>
            {data[0]?.date}
          </PrimaryText>
          <PrimaryText fontSize={12} style={{ color: "#666" }}>
            {data[data.length - 1]?.date}
          </PrimaryText>
        </div>
      </div>
    );
  };

  return (
    <Card style={{ padding: isMobile ? 12 : 20 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
        <PrimaryText fontSize={isMobile ? 16 : 20} fontWeight={700}>
          Retention Trends
        </PrimaryText>
        <InfoTooltip
          title="Track retention over time"
          content="Monitor how retention rates change over time. Use cohort comparison to see if product improvements are working."
        />
      </div>

      {trendData.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <PrimaryText>No trend data available</PrimaryText>
        </div>
      ) : (
        <div style={{ width: "100%" }}>
          <LineChart
            data={trendData}
            dataKey="weeklyActiveUsers"
            color="#2196f3"
            label="Weekly Active Users"
          />
          <LineChart
            data={trendData}
            dataKey="newUsers"
            color="#4caf50"
            label="New Users Per Day"
          />
          <LineChart
            data={trendData}
            dataKey="activeUsers"
            color="#ff9800"
            label="Active Users"
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", marginRight: isMobile ? 0 : 20 }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#2196f3",
                  marginRight: 8,
                }}
              ></div>
              <PrimaryText fontSize={12}>Weekly Active</PrimaryText>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", marginRight: isMobile ? 0 : 20 }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#4caf50",
                  marginRight: 8,
                }}
              ></div>
              <PrimaryText fontSize={12}>New Users</PrimaryText>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#ff9800",
                  marginRight: 8,
                }}
              ></div>
              <PrimaryText fontSize={12}>Active Users</PrimaryText>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default RetentionTrends;
